import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { SITE } from "@/config/site";
import { MEDIA, GALLERY } from "@/data/media";
import { PRODUCTS, formatPrice } from "@/data/products";
import { faqList } from "@/data/faqs";
import { Seo, JsonLd, businessLd, websiteLd } from "@/components/site/Seo";
import { Container, Eyebrow, Img, PhotoFrame, QuoteButton, Reveal, SectionHeading, TextLink, WhatsAppButton } from "@/components/site/Primitives";
import { CatalogueRequest, FaqSection, FinalCta, HowToOrder } from "@/components/site/Blocks";

const OFFER = [
  { n: "01", title: "Printing & Branding", text: "Custom printing, DTF, heat transfer and promotional branding enquiries.", to: "/printing-branding", media: MEDIA.mugsEvent },
  { n: "02", title: "Custom Apparel", text: "T-shirts, golf shirts, uniforms and jackets.", to: "/custom-tshirt-printing", media: MEDIA.golfShirtClan },
  { n: "03", title: "Personalised Gifts", text: "Mugs, tumblers, cushions, candles and other custom items.", to: "/personalised-gifts", media: MEDIA.cushionsTote },
  { n: "04", title: "Xhosa Cultural Products", text: "Ingcawe/Ngcawe blankets, Ibhayi lombhaco and related culturally meaningful products.", to: "/xhosa-cultural-products", media: MEDIA.blanketMngwevu },
];

const APPAREL = ["Custom T-shirts", "Branded golf shirts", "Uniforms", "Jackets", "Team clothing", "Church & group clothing"];

const WHY = [
  { t: "Personalised service", d: "Products designed around your requirements — your names, logos, messages and designs." },
  { t: "Wide product range", d: "Printing, branded clothing, personalised gifts and cultural products under one brand." },
  { t: "Cultural authenticity", d: "Products that celebrate Xhosa heritage, personalised with care and respect." },
  { t: "Local business", d: "A women-led Cape Town business serving individuals, families and organisations." },
];

const GIFTS = [PRODUCTS.mug, PRODUCTS.cushion, PRODUCTS.candle, PRODUCTS.tumbler, PRODUCTS.waterBottle];

export default function Home() {
  return (
    <>
      <Seo
        title="Printing and Branding Cape Town | Custom T-Shirts & Gifts | KhozaLand"
        description="KhozaLand is a women-led printing and branding company in Mitchells Plain, Cape Town: custom T-shirts, branded clothing, uniforms, corporate gifts, personalised gifts and Xhosa cultural products."
        path="/"
      />
      <JsonLd data={businessLd()} />
      <JsonLd data={websiteLd()} />

      {/* HERO */}
      <section className="relative overflow-hidden bg-white" data-testid="home-hero">
        <div className="kl-hero-wash" aria-hidden="true" />
        <Container className="relative grid gap-12 pb-16 pt-10 lg:grid-cols-12 lg:items-center lg:gap-10 lg:pb-24 lg:pt-16">
          <div className="lg:col-span-6">
            <Eyebrow>Printing & Branding · Cape Town</Eyebrow>
            <h1 className="kl-h1 mt-5">
              Your Ideas. Your Brand.<br />
              <em className="font-display italic">Brought to Life.</em>
            </h1>
            <p className="mt-6 max-w-xl text-base leading-7 text-black/70 sm:text-lg sm:leading-8">
              Custom printing, branded clothing, personalised gifts and traditional Xhosa products in Cape Town. KhozaLand helps individuals, businesses and organisations turn their ideas into reality.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <QuoteButton label="Request a Branding Quote" className="h-12 px-6" testId="home-hero-primary-cta" />
              <a href="#what-we-offer" className="kl-btn kl-btn-outline h-12 px-6" data-testid="home-hero-explore-cta">Explore Our Products</a>
            </div>
            <div className="mt-10 flex items-center gap-5 border-t border-black/10 pt-6">
              <p className="font-display text-2xl tracking-tight sm:text-[28px]" data-testid="home-hero-motto">{SITE.motto}</p>
              <span className="h-8 w-px bg-black/15" aria-hidden="true" />
              <p className="text-xs leading-5 text-black/60">100% women-led<br />Mitchells Plain, Cape Town</p>
            </div>
          </div>

          <div className="lg:col-span-6">
            <div className="grid grid-cols-5 gap-3 sm:gap-4">
              <div className="col-span-3">
                <PhotoFrame media={MEDIA.blanketRhudulu} ratio="4/5" eager sizes="(min-width:1024px) 30vw, 58vw" testId="hero-photo-main" />
                <p className="mt-2 text-[11px] text-black/50">Personalised Ingcawe blanket</p>
              </div>
              <div className="col-span-2 flex flex-col gap-3 pt-10 sm:gap-4 sm:pt-16">
                <PhotoFrame media={MEDIA.golfShirtClan} ratio="1/1" eager sizes="(min-width:1024px) 20vw, 38vw" />
                <PhotoFrame media={MEDIA.cushionsTote} ratio="1/1" sizes="(min-width:1024px) 20vw, 38vw" />
                <p className="text-[11px] text-black/50">Real KhozaLand work</p>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* WHAT WE OFFER */}
      <section id="what-we-offer" className="scroll-mt-24 bg-[#F7F7F7] py-16 lg:py-24" aria-labelledby="offer-title">
        <Container>
          <SectionHeading eyebrow="What we offer" title="Printing, apparel, gifts & heritage — under one brand" lead="Choose where you'd like to start. Every product can be customised with your names, logos, messages and designs." id="offer-title" />
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {OFFER.map((o, i) => (
              <Reveal key={o.to} delay={i * 70}>
                <Link to={o.to} className="kl-card group flex h-full flex-col overflow-hidden" data-testid={`service-card-${o.to.replace("/", "")}`}>
                  <div className="overflow-hidden border-b border-black/10" style={{ aspectRatio: "4/3" }}>
                    <Img media={o.media} sizes="(min-width:1024px) 22vw, (min-width:640px) 45vw, 92vw" className="kl-photo transition-transform duration-500 group-hover:scale-[1.04]" />
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <span className="font-display text-sm text-black/40">{o.n}</span>
                    <h3 className="mt-2 text-xl font-semibold tracking-tight">{o.title}</h3>
                    <p className="mt-2 text-sm leading-6 text-black/65">{o.text}</p>
                    <span className="mt-auto inline-flex items-center gap-1.5 pt-5 text-sm font-semibold" data-testid="service-card-explore-link">
                      <span className="kl-underline">Explore</span> <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden="true" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* FEATURED APPAREL */}
      <section className="py-16 lg:py-28" aria-labelledby="apparel-title" data-testid="home-apparel">
        <Container className="grid gap-12 lg:grid-cols-12 lg:items-center">
          <Reveal className="lg:col-span-6">
            <PhotoFrame media={MEDIA.golfShirtClan} ratio="4/3" sizes="(min-width:1024px) 45vw, 92vw" />
          </Reveal>
          <div className="lg:col-span-5 lg:col-start-8">
            <SectionHeading eyebrow="Custom apparel" title="Make Your Brand Stand Out." lead="Branded clothing that represents your business, team, church or family — printed with your logo, names or design." id="apparel-title" />
            <ul className="mt-8 grid grid-cols-2 gap-x-6 gap-y-3">
              {APPAREL.map((a) => (
                <li key={a} className="flex items-center gap-2.5 text-[15px]"><Check className="h-4 w-4 shrink-0" aria-hidden="true" /> {a}</li>
              ))}
            </ul>
            <div className="mt-8 grid grid-cols-3 divide-x divide-black/10 rounded-[14px] border border-black/10">
              {[PRODUCTS.roundNeckShort, PRODUCTS.golfShort, PRODUCTS.jacket].map((p) => (
                <div key={p.name} className="p-4">
                  <p className="text-[11px] leading-4 text-black/55">{p.name.split(" — ")[0]}</p>
                  <p className="mt-1 text-base font-semibold" data-testid="home-apparel-price">{formatPrice(p)}</p>
                </div>
              ))}
            </div>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <QuoteButton label="Request a Clothing Quote" category="custom-clothing" testId="home-apparel-quote-button" />
              <TextLink to="/custom-tshirt-printing" className="self-center sm:ml-2" testId="home-apparel-explore-link">See all clothing</TextLink>
            </div>
          </div>
        </Container>
      </section>

      {/* XHOSA CULTURAL */}
      <section className="bg-[#161616] py-16 text-white lg:py-28" aria-labelledby="heritage-title" data-testid="home-heritage">
        <Container className="grid gap-12 lg:grid-cols-12 lg:items-center">
          <div className="lg:col-span-5">
            <SectionHeading light eyebrow="Xhosa cultural products" title="Celebrate Your Heritage." id="heritage-title" />
            <p className="mt-5 text-base leading-7 text-white/70 sm:text-lg">
              Your clan name carries your family's story. KhozaLand personalises Ingcawe/Ngcawe blankets and clothing with clan names, iziduko and meaningful motifs — and takes enquiries for Ibhayi lombhaco — so you can wear and gift your identity with pride.
            </p>
            <ul className="mt-7 space-y-3 text-[15px] text-white/85">
              <li className="flex items-center gap-3"><span className="h-px w-6 bg-white/40" aria-hidden="true" /> Ingcawe / Ngcawe Blankets — {formatPrice(PRODUCTS.ingcawe)}</li>
              <li className="flex items-center gap-3"><span className="h-px w-6 bg-white/40" aria-hidden="true" /> Ibhayi lombhaco</li>
              <li className="flex items-center gap-3"><span className="h-px w-6 bg-white/40" aria-hidden="true" /> Personalised cultural products</li>
            </ul>
            <div className="mt-9 flex flex-col gap-3 sm:flex-row">
              <Link to="/xhosa-cultural-products" className="kl-btn kl-btn-light h-12 px-6" data-testid="home-heritage-explore-button">Explore Cultural Products <ArrowRight className="h-4 w-4" aria-hidden="true" /></Link>
              <WhatsAppButton messageKey="cultural" variant="ghost-light" className="h-12" label="WhatsApp an enquiry" testId="home-heritage-whatsapp-button" />
            </div>
          </div>
          <div className="grid grid-cols-2 items-start gap-4 lg:col-span-7">
            <Reveal><PhotoFrame media={MEDIA.blanketMngwevu} ratio="4/5" className="!bg-white/5 !border-white/10" sizes="(min-width:1024px) 28vw, 46vw" /></Reveal>
            <Reveal delay={90} className="pt-12"><PhotoFrame media={MEDIA.blanketRhudulu} ratio="4/5" className="!bg-white/5 !border-white/10" sizes="(min-width:1024px) 28vw, 46vw" /></Reveal>
          </div>
        </Container>
      </section>

      {/* PERSONALISED GIFTS */}
      <section className="py-16 lg:py-24" aria-labelledby="gifts-title" data-testid="home-gifts">
        <Container>
          <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <SectionHeading eyebrow="Personalised gifts" title="Make Every Gift Personal." lead="Names, photos, dates and messages — printed on gifts people keep." id="gifts-title" />
            <QuoteButton to="/personalised-gifts" label="Explore Personalised Gifts" variant="outline" testId="home-gifts-explore-button" />
          </div>
          <ul className="mt-10 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
            {GIFTS.map((g, i) => (
              <Reveal as="li" key={g.name} delay={i * 60}>
                <Link to="/personalised-gifts" className="group block" data-testid={`home-gift-${i}`}>
                  <div className="kl-mat">
                    <div className="kl-mat-inner" style={{ aspectRatio: "1/1" }}>
                      {g.media ? <Img media={g.media} sizes="(min-width:1024px) 18vw, 45vw" className="kl-photo transition-transform duration-500 group-hover:scale-[1.04]" /> : (
                        <div className="kl-placeholder grid h-full place-items-center"><img src="/images/khozaland-logo-sm.png" alt="" aria-hidden="true" className="w-16 opacity-25" loading="lazy" /></div>
                      )}
                    </div>
                  </div>
                  <p className="mt-3 text-[15px] font-semibold">{g.name}</p>
                  <p className="text-sm text-black/60">{formatPrice(g) || "Price on quotation"}</p>
                </Link>
              </Reveal>
            ))}
          </ul>
        </Container>
      </section>

      {/* WHY CHOOSE */}
      <section className="border-y border-black/10 bg-[#F7F7F7] py-16 lg:py-24" aria-labelledby="why-title">
        <Container>
          <SectionHeading eyebrow="Why KhozaLand" title="Why choose KhozaLand?" id="why-title" />
          <div className="mt-12 grid gap-px overflow-hidden rounded-[18px] border border-black/10 bg-black/10 sm:grid-cols-2 lg:grid-cols-4">
            {WHY.map((w, i) => (
              <Reveal key={w.t} delay={i * 70} className="bg-white p-7" >
                <span className="font-display text-3xl text-black/25">0{i + 1}</span>
                <h3 className="mt-5 text-lg font-semibold">{w.t}</h3>
                <p className="mt-2 text-sm leading-6 text-black/65">{w.d}</p>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* OUR WORK */}
      <section className="py-16 lg:py-24" aria-labelledby="work-title" data-testid="home-work">
        <Container>
          <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <SectionHeading eyebrow="Our work" title="Real products, made for real people" lead="A selection of genuine KhozaLand work — from cultural blankets to event gifts." id="work-title" />
            <QuoteButton to="/gallery" label="View Our Work" variant="outline" testId="home-view-work-button" />
          </div>
          <div className="mt-10 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
            {GALLERY.slice(0, 6).map((g, i) => (
              <Reveal key={g.id} delay={i * 60} className={i === 0 ? "lg:row-span-2" : ""}>
                <Link to="/gallery" className="group block h-full" data-testid={`home-work-item-${g.id}`}>
                  <div className={i === 0 ? "kl-mat lg:h-full" : "kl-mat"}>
                    <div className={i === 0 ? "kl-mat-inner aspect-[4/3] lg:aspect-auto lg:h-full" : "kl-mat-inner aspect-[4/3]"}>
                      <Img media={g.media} sizes="(min-width:1024px) 25vw, 46vw" className="kl-photo transition-transform duration-500 group-hover:scale-[1.03]" />
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
            <Reveal delay={240} className="col-span-2 lg:col-span-1">
              <Link to="/gallery" className="kl-card flex h-full min-h-[150px] flex-col justify-between bg-[#161616] p-6 text-white" data-testid="home-work-more-link">
                <p className="kl-eyebrow text-white/55">Gallery</p>
                <p className="font-display text-2xl">See more of our work <ArrowRight className="inline h-5 w-5" aria-hidden="true" /></p>
              </Link>
            </Reveal>
          </div>
        </Container>
      </section>

      <HowToOrder dark />

      {/* ABOUT */}
      <section className="py-16 lg:py-28" aria-labelledby="about-title" data-testid="home-about">
        <Container className="grid gap-12 lg:grid-cols-12 lg:items-center">
          <div className="lg:col-span-6">
            <SectionHeading eyebrow="About KhozaLand" title="A women-led business built on the power of dreams" id="about-title" />
            <p className="mt-5 text-base leading-7 text-black/70 sm:text-lg">
              KhozaLand was founded in 2020 by <strong className="font-semibold text-[#161616]">Yolanda Mketo</strong> in Mitchells Plain, Cape Town. What began as a passion for personalised products has grown into a printing and branding business serving individuals, families, businesses and community organisations.
            </p>
            <p className="mt-4 text-base leading-7 text-black/70">
              KhozaLand combines commercial branding with culturally meaningful products that celebrate Xhosa identity — and dreams of growing local manufacturing skills and opportunities for women and young people.
            </p>
            <TextLink to="/about" className="mt-7" testId="home-about-link">Discover Our Story</TextLink>
          </div>
          <div className="lg:col-span-5 lg:col-start-8">
            <figure className="kl-card relative overflow-hidden p-8 sm:p-10">
              <img src="/images/khozaland-logo-sm.png" alt="" aria-hidden="true" className="absolute -right-6 -top-4 w-44 opacity-[0.06]" loading="lazy" />
              <p className="kl-eyebrow text-black/50">Our mission</p>
              <blockquote className="mt-4 font-display text-2xl leading-snug tracking-tight sm:text-[28px]">Quality personalised printing and branding products that help customers express their identity and bring their ideas to life.</blockquote>
              <figcaption className="mt-6 text-sm"><span className="font-semibold">{SITE.legalName}</span><span className="text-black/55"> · Founded by {SITE.founder}</span></figcaption>
            </figure>
          </div>
        </Container>
      </section>

      <FaqSection faqs={faqList(["quote", "ownLogo", "bulk", "production", "deposit", "delivery"])} />
      <CatalogueRequest compact />
      <FinalCta />
    </>
  );
}
